import { Outlet } from "react-router-dom";

import { AppNavbar } from "@/app/layouts/dashboard/AppNavbar";
import { AppSidebar } from "@/app/layouts/dashboard/AppSidebar";
import { MobileSidebar } from "@/app/layouts/dashboard/MobileSidebar";
import { SidebarProvider } from "@/app/layouts/dashboard/sidebar-context";
import { useSidebar } from "@/app/layouts/dashboard/sidebar-context-core";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";

/**
 * Shell del backoffice — layout editorial.
 *
 * ## Breakpoints
 *
 *   - `<lg` (mobile/tablet): sidebar oculto por default, se abre como
 *     Sheet con el hamburger del navbar.
 *   - `lg+` (desktop): sidebar persistente, con toggle a modo icono
 *     (72px) + tooltips. Persistido en localStorage.
 *
 * ## Composición
 *
 *   - Sidebar sticky con h-svh en column izquierda, border sutil a la
 *     derecha.
 *   - Main area con Navbar sticky arriba (64px) + content scrolleable.
 *   - Content centrado con max-w-7xl y padding generoso. El fondo del
 *     content usa `bg-background` (brand `#F6F9F2`) — más neutro que
 *     `bg-arya-bg`, deja respirar las cards blancas.
 */
export function DashboardLayout() {
  return (
    <SidebarProvider>
      <TooltipProvider delayDuration={300} skipDelayDuration={100}>
        <Shell />
      </TooltipProvider>
    </SidebarProvider>
  );
}

function Shell() {
  const { collapsed } = useSidebar();

  return (
    <div className="flex min-h-svh bg-background">
      {/* Sidebar desktop (lg+) */}
      <aside
        className={cn(
          "sticky top-0 hidden h-svh shrink-0 transition-[width] duration-300 ease-in-out lg:block",
          collapsed ? "w-[72px]" : "w-64",
        )}
      >
        <AppSidebar variant="desktop" />
      </aside>

      {/* Sheet mobile (<lg) */}
      <MobileSidebar />

      {/* Área principal */}
      <div className="flex min-w-0 flex-1 flex-col">
        <AppNavbar />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8 lg:px-10 lg:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
